import React, { useState, useRef, Suspense, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card } from './ui/card';
import { AnalysisProgress } from './ui/AnalysisProgress';
import { FileUpload } from '@/components/ui/file-upload';
import { GlowEffect } from '@/components/ui/glow-effect';
import { RainbowButton } from '@/components/ui/rainbow-button';
import { useNavigate } from 'react-router-dom';
import { Rocket } from 'lucide-react';
import { motion } from 'framer-motion';
import { SearchableDropdown } from './ui/searchable-dropdown';
import { BorderTrail } from './ui/border-trail';
import { trackFeatureUsage, trackEvent } from '../analytics/posthog';

interface PitchDeckUploadProps {
    onUploadSuccess?: (response: any) => void;
    onUploadError?: (error: any) => void;
}

interface SubSector {
    id: number;
    name: string;
    industryId: number;
}

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes
const ACCEPTED_FILE_TYPES = {
    'application/pdf': ['.pdf'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
};

export const PitchDeckUpload: React.FC<PitchDeckUploadProps> = ({
    onUploadSuccess,
    onUploadError
}) => {
    const [files, setFiles] = useState<File[]>([]);
    const [companyName, setCompanyName] = useState('');
    const [subsectors, setSubsectors] = useState<SubSector[]>([]);
    const [selectedSubsector, setSelectedSubsector] = useState<SubSector | null>(null);
    const [isLoadingSubsectors, setIsLoadingSubsectors] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const [analysisId, setAnalysisId] = useState<number | undefined>(undefined);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const { api } = useAuth();
    const [isIndustryDropdownOpen, setIsIndustryDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        const fetchSubsectors = async () => {
            setIsLoadingSubsectors(true);
            try {
                const response = await api.get('/api/subsectors', {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                });
                if (!Array.isArray(response.data)) {
                    throw new Error('Expected array of subsectors but got: ' + typeof response.data);
                }
                setSubsectors(response.data);
            } catch (error: any) {
                console.error('Error fetching subsectors:', error);
                console.error('Error details:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status,
                    config: error.config
                });
                setError('Failed to load subsectors: ' + (error.response?.data?.error || error.message));
            } finally {
                setIsLoadingSubsectors(false);
            }
        };

        fetchSubsectors();
    }, [api]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsIndustryDropdownOpen(false);
            }
        }
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const startAnalysis = async () => {
        if (files.length === 0) {
            return;
        }

        // Reset any previous errors
        setError(null);

        if (!companyName.trim()) {
            setError("Company name is required");
            onUploadError?.({ message: "Company name is required" });
            return;
        }

        if (!selectedSubsector) {
            setError("Please select a subsector");
            onUploadError?.({ message: "Subsector is required" });
            return;
        }

        // Validate file type and size
        const file = files[0];
        if (!Object.keys(ACCEPTED_FILE_TYPES).includes(file.type)) {
            console.error('Invalid file type');
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            console.error('File too large');
            return;
        }

        try {
            setIsAnalyzing(true);
            
            // Track analysis started
            trackFeatureUsage('pitch_deck_upload', 'analysis_started', {
                company_name: companyName,
                subsector: selectedSubsector.name,
                subsector_id: selectedSubsector.id,
                file_type: file.type,
                file_size: file.size
            });

            const formData = new FormData();
            formData.append('file', file, file.name);
            formData.append('company_name', companyName);
            formData.append('subsector_id', selectedSubsector.id.toString());

            const response = await api.post<{
                analysis_id: number;
                analysis_data: null;
                interview_questions: any;
                investment_analysis: any;
                message: string;
            }>('/api/upload', formData, {
                withCredentials: true,
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'Origin': 'http://localhost:5173'
                },
                transformRequest: (data) => data
            });

            if (response.status < 200 || response.status >= 300) {
                throw new Error(`Upload failed with status ${response.status}`);
            }

            const { analysis_id } = response.data;
            setAnalysisId(analysis_id);
            
            // Track successful upload
            trackFeatureUsage('pitch_deck_upload', 'upload_success', {
                analysis_id,
                company_name: companyName,
                subsector: selectedSubsector.name
            });
            
            // Call onUploadSuccess with the correct ID
            if (onUploadSuccess) {
                onUploadSuccess({ analysis_id });
            }

        } catch (error: any) {
            console.error('Upload error:', error);
            const errorMessage = error.response?.data?.message || error.message || 'An error occurred during upload';
            setError(errorMessage);
            
            // Track upload failure
            trackFeatureUsage('pitch_deck_upload', 'upload_failed', {
                error_message: errorMessage,
                error_status: error.response?.status,
                company_name: companyName,
                subsector: selectedSubsector?.name
            });
            
            onUploadError?.(error);
            setIsAnalyzing(false);
        }
    };

    const handleFileUpload = (files: File[]) => {
        setFiles(files);
        // Track file selected
        if (files.length > 0) {
            trackFeatureUsage('pitch_deck_upload', 'file_selected', {
                file_type: files[0].type,
                file_size: files[0].size,
                file_name: files[0].name
            });
        }
    };

    if (isAnalyzing) {
        return (
            <Suspense fallback={
                <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div>Loading analysis...</div>
                </div>
            }>
                <AnalysisProgress startImmediately={true} analysisId={analysisId} />
            </Suspense>
        );
    }

    return (
        <Card className="w-full max-w-3xl mx-auto p-6 space-y-6 relative bg-white/50 backdrop-blur-sm">
            <BorderTrail 
                className="bg-gradient-to-l from-indigo-300 via-indigo-500 to-indigo-300 dark:from-indigo-700/30 dark:via-indigo-500 dark:to-indigo-700/30"
                size={120}
            />
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                    <span className="block sm:inline">{error}</span>
                </div>
            )}
            <div className="space-y-4">
                <div className="text-left">
                    <input
                        type="text"
                        placeholder="Enter Company Name *"
                        value={companyName}
                        onChange={(e) => {
                            setCompanyName(e.target.value);
                            setError(null);
                        }}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-left ${
                            error && !companyName.trim() ? 'border-red-500' : ''
                        }`}
                        required
                    />
                </div>
                <div className="form-group mb-4 relative" ref={dropdownRef}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Select Industry
                    </label>
                    <div 
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-pointer flex justify-between items-center"
                        onClick={() => setIsIndustryDropdownOpen(!isIndustryDropdownOpen)}
                    >
                        <span>{selectedSubsector?.name || "Select an industry"}</span>
                        <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                    
                    {isIndustryDropdownOpen && (
                        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                            <div 
                                className="cursor-pointer select-none relative py-2 pl-3 pr-9 text-gray-900 dark:text-gray-100 hover:bg-indigo-600 hover:text-white"
                                onClick={() => {
                                    setSelectedSubsector(null);
                                    setIsIndustryDropdownOpen(false);
                                }}
                            >
                                Select an industry
                            </div>
                            {subsectors.map((sector) => (
                                <div
                                    key={sector.id}
                                    className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                                        selectedSubsector?.name === sector.name 
                                            ? 'bg-indigo-600 text-white' 
                                            : 'text-gray-900 dark:text-gray-100 hover:bg-indigo-600 hover:text-white'
                                    }`}
                                    onClick={() => {
                                        setSelectedSubsector(sector);
                                        setIsIndustryDropdownOpen(false);
                                    }}
                                >
                                    {sector.name}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <FileUpload
                    onChange={handleFileUpload}
                    maxSize={MAX_FILE_SIZE}
                    accept={ACCEPTED_FILE_TYPES}
                    uploadMessage={
                        <span className="h3 text-bold">
                            Upload your pitch deck
                        </span>
                    }
                    uploadHint={
                        <span className="p-small">
                            Supports PDF, PPT, or PPTX files up to 20MB
                        </span>
                    }
                    className="text-foreground hover:bg-card/10 transition-colors"
                />
            </div>

            {files.length > 0 && isMounted && (
                <div className="flex justify-center mt-6">
                    <div className="relative">
                        <GlowEffect
                            colors={['#818CF8', '#4F46E5', '#6366F1', '#4338CA']}
                            mode="colorShift"
                            blur="soft"
                            duration={3}
                            scale={1}
                        />
                        <RainbowButton
                            onClick={startAnalysis}
                            disabled={isAnalyzing}
                            className="h-12 text-base font-semibold relative bg-primary hover:bg-primary/90"
                            aria-busy={isAnalyzing}
                        >
                            <span className="flex items-center justify-center gap-2 relative z-10 text-primary-foreground">
                                Let's Make Investors FOMO
                                <motion.div
                                    initial={{ rotate: -45 }}
                                    animate={{ rotate: [-45, -30, -45] }}
                                    transition={{
                                        duration: 1.5,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                >
                                    <Rocket className="w-4 h-4 transition-transform group-hover:scale-110" />
                                </motion.div>
                            </span>
                        </RainbowButton>
                    </div>
                </div>
            )}
        </Card>
    );
};
